import { useMemo } from 'react';
import { useAdminFormPage } from '../../hooks/useAdminFormPage';
import { AdminFormPage } from '../../components/AdminFormPage';
import { getWorkflowScheduleFormConfig } from '../../configs/workflowScheduleFormConfig';
import { workflowSchedulesService } from '../../api/workflowSchedulesService';
import type {
  StoreWorkflowScheduleRequest,
  UpdateWorkflowScheduleRequest,
} from '../../api/workflowSchedulesService';

interface ScheduleParams {
  id?: string;
}

export function WorkflowScheduleFormPage() {
  const formPage = useAdminFormPage<unknown, ScheduleParams>({
    entityName: 'schedule',
    getConfig: (mode, params) => getWorkflowScheduleFormConfig(mode, params.id),
    getEditId: (params) => params.id,
    queryKey: (params) => ['admin', 'workflows', 'schedules', params.id],
    fetchFn: (params) => workflowSchedulesService.get(params.id!),
    mapToInitialValues: (entity) => {
      const schedule = entity as Record<string, unknown>;
      return {
        workflow_definition_id: schedule.workflow_definition_id,
        name: schedule.name,
        description: schedule.description ?? '',
        cron_expression: schedule.cron_expression,
        timezone: schedule.timezone ?? 'UTC',
        is_active: schedule.is_active,
        input_data: schedule.input_data
          ? JSON.stringify(schedule.input_data, null, 2)
          : '{}',
        options: schedule.options
          ? JSON.stringify(schedule.options, null, 2)
          : '{}',
      };
    },
    mutationFn: async (payload, isEdit, params) => {
      const inputData = parseJsonField(payload.input_data as string | undefined);
      const options = parseJsonField(payload.options as string | undefined);

      if (isEdit && params.id) {
        const updatePayload: UpdateWorkflowScheduleRequest = {
          workflow_definition_id: payload.workflow_definition_id as string,
          name: payload.name as string,
          description: (payload.description as string) || undefined,
          cron_expression: payload.cron_expression as string,
          timezone: payload.timezone as string,
          is_active: payload.is_active as boolean,
          input_data: inputData,
          options: options,
        };
        return workflowSchedulesService.update(params.id, updatePayload);
      }
      const createPayload: StoreWorkflowScheduleRequest = {
        workflow_definition_id: payload.workflow_definition_id as string,
        name: payload.name as string,
        description: (payload.description as string) || undefined,
        cron_expression: payload.cron_expression as string,
        timezone: (payload.timezone as string) || 'UTC',
        is_active: (payload.is_active as boolean) ?? true,
        input_data: inputData,
        options: options,
      };
      return workflowSchedulesService.create(createPayload);
    },
    invalidateKey: () => ['admin', 'workflows', 'schedules'],
    navigateBackPath: () => '/admin/workflows/schedules',
  });

  const breadcrumbs = useMemo(
    () => [
      { label: 'Admin', href: '/admin' },
      { label: 'Schedules', href: '/admin/workflows/schedules' },
      { label: formPage.isEdit ? 'Edit Schedule' : 'Create Schedule' },
    ],
    [formPage.isEdit],
  );

  const errorBreadcrumbs = useMemo(
    () => [
      { label: 'Admin', href: '/admin' },
      { label: 'Schedules', href: '/admin/workflows/schedules' },
      { label: 'Edit' },
    ],
    [],
  );

  return (
    <AdminFormPage
      formPage={formPage}
      entityLabel="Schedule"
      breadcrumbs={breadcrumbs}
      errorBreadcrumbs={errorBreadcrumbs}
      genericFetchErrorMessage="An unexpected error occurred while loading the schedule."
    />
  );
}

/**
 * Safely parses a JSON string field, returning undefined if the input
 * is empty or not valid JSON.
 */
function parseJsonField(value: string | undefined): Record<string, unknown> | undefined {
  if (!value || value.trim() === '' || value.trim() === '{}') return undefined;
  try {
    return JSON.parse(value) as Record<string, unknown>;
  } catch {
    return undefined;
  }
}
